import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  memo,
} from 'react';
import { useIntl } from 'react-intl';
import debounce from 'lodash.debounce';

import CustomInput from '../../../generic/CustomInput/CustomInput';
import CustomBtn from '../../../generic/CustomBtn/CustomBtn';
import { CustomReactSelect as CustomSelect } from '../../../generic/CustomSelect/CustomSelect';
import {
  REGISTER_STATUS,
  ACTIVE_STATUS,
  PAUSED_STATUS,
  CLOSED_STATUS,
  FAILED_STATUS,
  TERMINATED_STATUS,
} from '../../../constans/constans';
import './Filter.scss';

const selectOptions = [
  { value: 'mrv', label: 'MRV' },
  { value: 'carbon', label: 'Carbon' },
];

const Filter = ({ setFilterQuery, onReset = () => {} }) => {
  const translate = useIntl();
  const [nameValue, setNameValue] = useState('');
  const [locationValue, setLocationValue] = useState('');
  const [mrvOrCarbon, setMrvOrCarbon] = useState('');
  const [statusValue, setStatusValue] = useState('');
  const [isActive, setIsActive] = useState(false);

  const nameRef = useRef();
  const locationRef = useRef();
  const mrvOrCarbonRef = useRef();
  const statusRef = useRef();

  const statusOptions = [
    { value: REGISTER_STATUS, label: translate.formatMessage({ id: 'status.register' }) },
    { value: ACTIVE_STATUS, label: translate.formatMessage({ id: 'status.active' }) },
    { value: PAUSED_STATUS, label: translate.formatMessage({ id: 'status.paused' }) },
    { value: CLOSED_STATUS, label: translate.formatMessage({ id: 'status.closed' }) },
    { value: FAILED_STATUS, label: translate.formatMessage({ id: 'status.failed' }) },
    { value: TERMINATED_STATUS, label: translate.formatMessage({ id: 'status.terminated' }) },
  ];

  const selectStyles = {
    control: (styles) => ({
      ...styles,
      padding: 10,
      fontSize: 12,
      lineHeight: '22px',
      color: '#5b6075',
      borderRadius: 8,
      backgroundColor: 'white',
      border: '1px solid #d5d9e7',
      boxShadow: 0,
      cursor: 'pointer',
      '&:hover': {
        boxShadow: 'none',
      },
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: 0,
      fontSize: 12,
      lineHeight: '22px',
    }),
    placeholder: (styles) => ({
      ...styles,
      margin: 0,
      fontSize: 12,
      lineHeight: '22px',
      fontWeight: 400,
      color: '#5b6075',
    }),
    singleValue: (styles) => ({
      ...styles,
      margin: 0,
      fontSize: 12,
      lineHeight: '22px',
      fontWeight: 400,
      color: '#5b6075',
    }),
    dropdownIndicator: (styles, { selectProps: { menuIsOpen } }) => ({
      ...styles,
      padding: 0,
      transform: menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      color: '#d5d9e7',
      svg: {
        width: 22,
        height: 22,
      },
    }),
    option: (styles, { isFocused }) => ({
      ...styles,
      backgroundColor: isFocused ? 'rgba(50, 157, 156, 0.2);' : 'white',
      color: '#5b6075',
      fontSize: 12,
      lineHeight: '22px',
      paddingLeft: 10,
      cursor: 'pointer',
    }),
  };

  const resetFilter = () => {
    nameRef.current.value = '';
    locationRef.current.value = '';
    mrvOrCarbonRef.current.select.clearValue();
    statusRef.current.select.clearValue();

    setNameValue('');
    setLocationValue('');
    setMrvOrCarbon('');
    setStatusValue('');
    onReset();
  };

  const isCarbonProject = (value) => value === 'carbon';

  const matchPhrasePrefixQuery = (key, value) => ({
    match_phrase_prefix: {
      [key]: {
        query: value,
      },
    },
  });

  const matchQuery = (key, value) => ({
    match: {
      [key]: value,
    },
  });

  const mustQuery = (params) => {
    const q = [];
    params.forEach(({ key, active, searchValue, subQuery }) => {
      if (active) q.push(subQuery(key, searchValue));
    });

    return q;
  };

  const setFilterWithDelay = useCallback(debounce((filterQuery) => {
    if (filterQuery.length) {
      setFilterQuery(filterQuery);
      return;
    }
    setFilterQuery();
  }, 500), []);

  useEffect(() => {
    const filterParams = [
      { key: 'name', active: !!nameValue, searchValue: nameValue, subQuery: matchPhrasePrefixQuery },
      { key: 'region', active: !!locationValue, searchValue: locationValue, subQuery: matchPhrasePrefixQuery },
      { key: 'isCarbonProject', active: !!mrvOrCarbon, searchValue: isCarbonProject(mrvOrCarbon), subQuery: matchQuery },
      { key: 'status.keyword', active: !!statusValue, searchValue: statusValue, subQuery: matchQuery },
    ];

    const filterQuery = mustQuery(filterParams);
    setIsActive(!!filterQuery.length);
    setFilterWithDelay(filterQuery);
  }, [
    nameValue,
    locationValue,
    mrvOrCarbon,
    statusValue,
  ]);

  return (
    <div className="filter">
      <CustomInput
        type="text"
        label={translate.formatMessage({ id: 'filter.project.label' })}
        placeholder={translate.formatMessage({ id: 'filter.project.placeholder' })}
        change={(e) => setNameValue(e.target.value)}
        customClass="filter__name"
        customClassLabel="filter__label"
        register={nameRef}
      />
      <CustomInput
        type="text"
        label={translate.formatMessage({ id: 'filter.location.label' })}
        placeholder={translate.formatMessage({ id: 'filter.location.placeholder' })}
        change={(e) => setLocationValue(e.target.value)}
        customClass="filter__location"
        customClassLabel="filter__label"
        register={locationRef}
      />
      <CustomSelect
        labelText={translate.formatMessage({ id: 'filter.mrvCarbon.label' })}
        placeholder={translate.formatMessage({ id: 'filter.mrvCarbon.placeholder' })}
        options={selectOptions}
        selectOnChange={setMrvOrCarbon}
        wrapperClass="filter__mrv-carbon"
        titleClass="filter__label"
        selectStyles={selectStyles}
        ref={mrvOrCarbonRef}
      />
      <CustomSelect
        labelText={translate.formatMessage({ id: 'filter.status.label' })}
        placeholder={translate.formatMessage({ id: 'filter.status.placeholder' })}
        options={statusOptions}
        selectOnChange={setStatusValue}
        wrapperClass="filter__status"
        titleClass="filter__label"
        selectStyles={selectStyles}
        ref={statusRef}
      />
      <CustomBtn
        label={translate.formatMessage({ id: 'button-resetFilters' })}
        customClass={`filter__reset ${isActive ? 'active' : ''}`}
        handleClick={resetFilter}
      />
    </div>
  );
};

export default memo(Filter);
